import {forwardRef, useEffect, useState} from "react";
import {FSImageRef} from "../../../ui/fullscreen/image";
import {FSVideoRef} from "../../../ui/fullscreen/video";
import {PlayerReduxState} from "../../../redux/player/types";
import {getCurrentAction} from "../../../redux/player/selectors";
import {Action, ActionWithMedia, isActionWithMedia} from "../../../types";
import SystemActionContent from "./content/SystemActionContent";
import {useDispatch, useSelector} from "react-redux";
import {isEqual} from "lodash";
import {parseMimeType, PlayableMediaType} from "../../../utils";
import Content from "./content/Content";
import classes from "./MediaDisplay.module.css";
import cn from "classnames";

type MediaDisplayProps = {
  openFullscreenMedia: React.ComponentProps<typeof SystemActionContent>["openFullscreenMedia"];
  "data-testId"?: string
};

type SelectedType = {
  action: Action | null;
  onVideoPlay: boolean;
};

type MediaDisplayType = React.ForwardRefExoticComponent<
  MediaDisplayProps & React.RefAttributes<FSImageRef | FSVideoRef>
> & {
  selector?: (state: PlayerReduxState) => SelectedType;
};

type MediaDisplayContentProps = {
  action: ActionWithMedia;
  onVideoPlay: boolean;
  openFullscreenMedia: React.ComponentProps<typeof SystemActionContent>["openFullscreenMedia"];
  setOrientation: React.Dispatch<React.SetStateAction<Orientation | null>>;
};

type MediaDisplayContentType = React.ForwardRefExoticComponent<
  MediaDisplayContentProps & React.RefAttributes<FSImageRef | FSVideoRef>
>;

enum Orientation {
  HORIZONTAL,
  VERTICAL,
}

const MediaDisplayContent: MediaDisplayContentType = forwardRef(
  ({action, onVideoPlay, openFullscreenMedia, setOrientation}, ref) => {
    const dispatch = useDispatch();
    const {media, mediaType} = action;
    const mimeType = parseMimeType(mediaType).type;
    const mediaId = "action-media";

    // Define media orientation
    useEffect(() => {
      setOrientation(null);

      if (mimeType === PlayableMediaType.VIDEO) {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.src = media;
        video.onloadedmetadata = () => {
          const orientation = video.videoHeight > video.videoWidth ? Orientation.VERTICAL : Orientation.HORIZONTAL;
          setOrientation(orientation);
        };
      }

      if (mimeType === PlayableMediaType.IMAGE) {
        setOrientation(Orientation.HORIZONTAL);
      }
    }, [media, mimeType, setOrientation]);

    // Start video
    useEffect(() => {
      if (mimeType !== PlayableMediaType.VIDEO) return;

      const video = document.getElementById(mediaId) as HTMLVideoElement | null;

      if (video) {
        if (onVideoPlay) video.play().catch(console.error);

        const handleEnded = () => {
          dispatch({type: "resetVideoPlayback"});
        };

        video.addEventListener("ended", handleEnded);

        return () => {
          video.removeEventListener("ended", handleEnded);
        };
      } else {
        console.warn(`Cannot play: <video id="${mediaId}"> not found in DOM`);
      }
    }, [onVideoPlay, mimeType, dispatch]);

    const mediaProps = {
      onClick: openFullscreenMedia,
      src: media,
      id: mediaId,
      ref,
    };

    switch (mimeType) {
      case PlayableMediaType.IMAGE:
        return (
          <div className={classes.image}>
            <Content.Image {...mediaProps} className={cn(classes.media, classes.mediaWithFS)} openable />
          </div>
        );
      case PlayableMediaType.VIDEO:
        return <Content.Video {...mediaProps} className={classes.media} />;
      default:
        console.error(`Unsupported media type in content: ${mimeType}`);
        return null;
    }
  },
);

const MediaDisplay: MediaDisplayType = forwardRef(({openFullscreenMedia, "data-testId": dataTestId}, ref) => {
  const {action, onVideoPlay} = useSelector(MediaDisplay.selector!, isEqual);
  const [orientation, setOrientation] = useState<Orientation | null>(null);

  if (!isActionWithMedia(action)) return null;

  const className = cn(classes.root, {
    [classes.verticalMedia]: orientation === Orientation.VERTICAL,
    [classes.horizontalMedia]: orientation === Orientation.HORIZONTAL,
  });

  return (
    <div className={className} data-testId={dataTestId}>
      <MediaDisplayContent
        action={action}
        onVideoPlay={onVideoPlay}
        openFullscreenMedia={openFullscreenMedia}
        setOrientation={setOrientation}
        ref={ref}
      />
    </div>
  );
});

export default MediaDisplay;

MediaDisplay.selector = (state: PlayerReduxState) => ({
  action: getCurrentAction(state),
  onVideoPlay: state.onVideoPlay,
});
